import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ChevronLeft, 
  Settings, 
  Plus, 
  Pencil, 
  Archive, 
  RefreshCw, 
  Search, 
  BookOpen,
  Layout
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";
import AccessRestricted from "../components/AccessRestricted";
import { useData } from "../context/DataContext";
import { MultiSelect } from "../components/ui/multi-select";
import { Combobox } from "../components/ui/combobox";
import { TrainingPlanTemplate, Procedure, JobRole } from "../types";
import { getMatchedObjects, toSydneyDate } from "../utils/helpers";

const TrainingPlanSettings: React.FC = () => {
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();
  const { 
    trainingPlanTemplates, 
    procedures, 
    jobRoles,
    addTrainingPlanTemplate,
    updateTrainingPlanTemplate,
    archiveTrainingPlanTemplate,
    unarchiveTrainingPlanTemplate
  } = useData();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("templates");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Template Dialog State
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TrainingPlanTemplate | null>(null);
  const [formData, setFormData] = useState({
    templateName: "",
    description: "",
    jobRoleId: undefined as string | undefined,
    procedureIds: [] as string[]
  });

  if (!ability.can(ACTIONS.READ, RESOURCES.TRAINING_PLAN_SETTINGS)) {
    return <AccessRestricted />;
  }

  const filteredTemplates = trainingPlanTemplates.filter(t => 
    t.templateName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenCreate = () => {
    setEditingTemplate(null);
    setFormData({
      templateName: "",
      description: "",
      jobRoleId: undefined,
      procedureIds: []
    });
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (template: TrainingPlanTemplate) => {
    setEditingTemplate(template);
    setFormData({
      templateName: template.templateName,
      description: template.description || "",
      jobRoleId: template.jobRole?.id,
      procedureIds: template.procedures.map(p => p.id)
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const templateData: Partial<TrainingPlanTemplate> = {
        templateName: formData.templateName,
        description: formData.description,
        jobRole: jobRoles.find(r => r.id === formData.jobRoleId),
        procedures: getMatchedObjects(formData.procedureIds, procedures)
      };

      if (editingTemplate) {
        await updateTrainingPlanTemplate(editingTemplate.id, templateData);
      } else {
        await addTrainingPlanTemplate(templateData);
      }
      
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Error saving template:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            <Settings className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold">Training Plan Settings</h1>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-gray-100 p-1 rounded-full">
          <TabsTrigger value="templates" className="rounded-full px-6 data-[state=active]:bg-white">
            <Layout className="w-4 h-4 mr-2" />
            Templates
          </TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="space-y-4 pt-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button onClick={handleOpenCreate} className="w-full md:w-auto">
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </Button>
          </div>

          <div className="border rounded-lg overflow-hidden bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Template Name</TableHead>
                  <TableHead>Job Role</TableHead>
                  <TableHead>Procedures</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created On</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTemplates.length > 0 ? (
                  filteredTemplates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell className="font-medium">{template.templateName}</TableCell>
                      <TableCell>{template.jobRole?.title || "N/A"}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {template.procedures.length} Procedures
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={template.status === "Active" ? "outline" : "secondary"}>
                          {template.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{toSydneyDate(template.createdAt)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="outline" 
                            size="icon" 
                            onClick={() => handleOpenEdit(template)}
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {template.status === "Active" ? (
                            <Button 
                              variant="outline" 
                              size="icon" 
                              onClick={() => archiveTrainingPlanTemplate(template.id)}
                              title="Archive"
                            >
                              <Archive className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button 
                              variant="outline" 
                              size="icon" 
                              onClick={() => unarchiveTrainingPlanTemplate(template.id)}
                              title="Unarchive"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      No templates found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? "Edit Template" : "Create New Template"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="templateName">Template Name</Label>
              <Input
                id="templateName"
                value={formData.templateName}
                onChange={(e) => setFormData({ ...formData, templateName: e.target.value })}
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="jobRole">Associated Job Role (Optional)</Label>
              <Combobox
                options={jobRoles.map(role => ({
                  label: role.title,
                  value: role.id
                }))}
                value={formData.jobRoleId}
                onChange={(val) => setFormData({ ...formData, jobRoleId: val })}
                placeholder="Select a Job Role"
              />
            </div>

            <div className="grid gap-2">
              <Label>Included Procedures</Label>
              <MultiSelect
                options={procedures.map(p => ({
                  label: p.procedureName,
                  value: p.id
                }))}
                selected={formData.procedureIds}
                onChange={(vals) => setFormData({ ...formData, procedureIds: vals })}
                placeholder="Select procedures..."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : (editingTemplate ? "Update Template" : "Create Template")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrainingPlanSettings;
